import type { Element, Point } from '@/types';

/**
 * Checks if a point is on one of the resize handles of any selected element.
 * @param point - The point to check.
 * @param elements - All elements on the canvas.
 * @param selectedIds - IDs of the selected elements.
 * @returns The element and the specific handle ('top-left', 'bottom-right', etc.) if found, otherwise null.
 */
export function getResizeHandleAtPoint(
  point: Point,
  elements: Element[],
  selectedIds: string[]
): { element: Element; handle: string } | null {
  const selectedElements = elements.filter(el => selectedIds.includes(el.id));
  for (const element of selectedElements) {
    const handle = getResizeHandleForElement(point, element);
    if (handle) {
      return { element, handle };
    }
  }
  return null;
}

/**
 * Calculates the updates needed for a batch of elements during a multi-element resize.
 * @param initialState - The initial state (position and dimensions) of the element.
 * @param bounds - The initial bounding box of the entire selection.
 * @param scaleX - The horizontal scaling factor.
 * @param scaleY - The vertical scaling factor.
 * @returns The partial element updates.
 */
export function calculateMultiResizeUpdates(
  initialState: any,
  bounds: { x: number; y: number; width: number; height: number },
  scaleX: number,
  scaleY: number
): any {
  const relativeX = initialState.x - bounds.x;
  const relativeY = initialState.y - bounds.y;

  const newX = bounds.x + relativeX * scaleX;
  const newY = bounds.y + relativeY * scaleY;

  if (initialState.type === 'ellipse') {
    const newRadiusX = initialState.radiusX * scaleX;
    const newRadiusY = initialState.radiusY * scaleY;
    return { x: newX, y: newY, radiusX: newRadiusX, radiusY: newRadiusY };
  } else {
    const newWidth = initialState.width * scaleX;
    const newHeight = initialState.height * scaleY;
    return { x: newX, y: newY, width: newWidth, height: newHeight };
  }
}

/**
 * Calculates the updates for a single element being resized.
 * @param initialState - The initial state of the element.
 * @param handle - The resize handle being dragged.
 * @param deltaX - The horizontal drag distance.
 * @param deltaY - The vertical drag distance.
 * @returns The partial element updates.
 */
export function calculateSingleResizeUpdates(
  initialState: any,
  handle: string,
  deltaX: number,
  deltaY: number
): any {
  // Fast path for icons - optimized rectangle resize without extra logic
  if (initialState.type === 'icon') {
    return calculateIconResize(initialState, handle, deltaX, deltaY);
  }

  switch (initialState.type) {
    case 'rectangle':
    case 'diamond':
    case 'svg':
    case 'image':
      return calculateRectangleResize(initialState, handle, deltaX, deltaY);
    case 'ellipse':
      return calculateEllipseResize(initialState, handle, deltaX, deltaY);
    case 'arrow':
      return calculateArrowResize(initialState, handle, deltaX, deltaY);
    case 'text':
      return calculateTextResize(initialState, handle, deltaX, deltaY);
    case 'group':
      return calculateGroupResize(initialState, handle, deltaX, deltaY);
    default:
      return {};
  }
}

// --- Private Helper Functions ---

function getResizeHandleForElement(
  point: Point,
  element: Element
): string | null {
  const handleSize = 8;
  const halfHandleSize = handleSize / 2;

  const bounds = getElementBounds(element);
  const { x, y, width, height } = bounds;

  const handles = {
    'top-left': { x: x - halfHandleSize, y: y - halfHandleSize },
    'top-right': { x: x + width - halfHandleSize, y: y - halfHandleSize },
    'bottom-left': { x: x - halfHandleSize, y: y + height - halfHandleSize },
    'bottom-right': {
      x: x + width - halfHandleSize,
      y: y + height - halfHandleSize,
    },
    'top-center': { x: x + width / 2 - halfHandleSize, y: y - halfHandleSize },
    'bottom-center': {
      x: x + width / 2 - halfHandleSize,
      y: y + height - halfHandleSize,
    },
    'left-center': {
      x: x - halfHandleSize,
      y: y + height / 2 - halfHandleSize,
    },
    'right-center': {
      x: x + width - halfHandleSize,
      y: y + height / 2 - halfHandleSize,
    },
  };

  for (const [handle, pos] of Object.entries(handles)) {
    if (
      point.x >= pos.x &&
      point.x <= pos.x + handleSize &&
      point.y >= pos.y &&
      point.y <= pos.y + handleSize
    ) {
      return handle;
    }
  }

  return null;
}

function getElementBounds(element: Element): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  if (element.type === 'ellipse') {
    return {
      x: element.x,
      y: element.y,
      width: element.radiusX * 2,
      height: element.radiusY * 2,
    };
  }
  if (element.type === 'line' || element.type === 'arrow') {
    const { points } = element;
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  if (element.type === 'group') {
    return {
      x: element.x,
      y: element.y,
      width: (element as any).width,
      height: (element as any).height,
    };
  }
  return {
    x: element.x,
    y: element.y,
    width: (element as any).width,
    height: (element as any).height,
  };
}

function calculateRectangleResize(
  rect: any,
  handle: string,
  deltaX: number,
  deltaY: number
): any {
  let { x, y, width, height } = rect;

  if (handle.includes('left')) {
    x += deltaX;
    width -= deltaX;
  }
  if (handle.includes('right')) {
    width += deltaX;
  }
  if (handle.includes('top')) {
    y += deltaY;
    height -= deltaY;
  }
  if (handle.includes('bottom')) {
    height += deltaY;
  }

  // Flip correction
  if (width < 0) {
    x += width;
    width = Math.abs(width);
  }
  if (height < 0) {
    y += height;
    height = Math.abs(height);
  }

  return { x, y, width, height };
}

function calculateEllipseResize(
  ellipse: any,
  handle: string,
  deltaX: number,
  deltaY: number
): any {
  let { x, y, radiusX, radiusY } = ellipse;

  if (handle.includes('left')) {
    x += deltaX;
    radiusX -= deltaX / 2;
  }
  if (handle.includes('right')) {
    radiusX += deltaX / 2;
  }
  if (handle.includes('top')) {
    y += deltaY;
    radiusY -= deltaY / 2;
  }
  if (handle.includes('bottom')) {
    radiusY += deltaY / 2;
  }

  return { x, y, radiusX, radiusY };
}

function calculateArrowResize(
  arrow: any,
  handle: string,
  deltaX: number,
  deltaY: number
): any {
  const newPoints = [...arrow.points];

  if (handle === 'start') {
    // Special handle for line/arrow start
    newPoints[0] = { x: newPoints[0].x + deltaX, y: newPoints[0].y + deltaY };
  } else if (handle === 'end') {
    // Special handle for line/arrow end
    const lastIndex = newPoints.length - 1;
    newPoints[lastIndex] = {
      x: newPoints[lastIndex].x + deltaX,
      y: newPoints[lastIndex].y + deltaY,
    };
  }

  return { points: newPoints };
}

function calculateTextResize(
  text: any,
  _handle: string,
  deltaX: number,
  deltaY: number
): any {
  return { ...text, width: text.width + deltaX, height: text.height + deltaY };
}

/**
 * Optimized icon resize calculation for better performance
 */
function calculateIconResize(
  icon: any,
  handle: string,
  deltaX: number,
  deltaY: number
): any {
  let { x, y, width, height } = icon;

  // For icons, maintain aspect ratio for corner handles
  if (handle.includes('left') && handle.includes('top')) {
    // Top-left: resize both dimensions, maintain aspect ratio
    const delta = Math.min(deltaX, deltaY);
    x += delta;
    y += delta;
    width -= delta;
    height -= delta;
  } else if (handle.includes('right') && handle.includes('bottom')) {
    // Bottom-right: resize both dimensions, maintain aspect ratio
    const delta = Math.max(deltaX, deltaY);
    width += delta;
    height += delta;
  } else if (handle.includes('left') && handle.includes('bottom')) {
    // Bottom-left: resize both dimensions, maintain aspect ratio
    const delta = Math.min(-deltaX, deltaY);
    x -= delta;
    width += delta;
    height += delta;
  } else if (handle.includes('right') && handle.includes('top')) {
    // Top-right: resize both dimensions, maintain aspect ratio
    const delta = Math.min(deltaX, -deltaY);
    y -= delta;
    width += delta;
    height += delta;
  } else {
    // Side handles: resize individual dimensions
    if (handle.includes('left')) {
      x += deltaX;
      width -= deltaX;
    }
    if (handle.includes('right')) {
      width += deltaX;
    }
    if (handle.includes('top')) {
      y += deltaY;
      height -= deltaY;
    }
    if (handle.includes('bottom')) {
      height += deltaY;
    }
  }

  // Ensure minimum size
  const minSize = 10;
  if (width < minSize) {
    x -= (minSize - width) / 2;
    width = minSize;
  }
  if (height < minSize) {
    y -= (minSize - height) / 2;
    height = minSize;
  }

  return { x, y, width, height };
}

function calculateGroupResize(
  group: any,
  handle: string,
  deltaX: number,
  deltaY: number
): any {
  // Groups resize like rectangles
  return calculateRectangleResize(group, handle, deltaX, deltaY);
}
